import { getAuthSession, handleUnauthorizedSession } from '@/services/authService';

export const canViewPropostas = (session) => {
  const role = String(session?.tipoUsuario?.nomeTipoUsuario || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
  return ['admin', 'administrador', 'administrator'].includes(role);
};

const requestPropostas = async (path, signal, { method = 'GET', body } = {}) => {
  const token = getAuthSession()?.token;
  if (!token) {
    handleUnauthorizedSession();
    throw new Error('Sessão expirada. Faça login novamente.');
  }

  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  const timeout = setTimeout(abort, 20000);
  try {
    const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
    const response = await fetch(`${baseUrl}/propostas${path}`, {
      method,
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    if (response.status === 401) {
      handleUnauthorizedSession();
      throw new Error('Sessão expirada. Faça login novamente.');
    }
    if (response.status === 403) throw new Error('Seu usuário não tem acesso às propostas.');
    if (response.status === 404) {
      const isList = !path || path.startsWith('?');
      throw new Error(isList
        ? 'A consulta de propostas não está disponível na API conectada. Verifique se o backend está atualizado.'
        : 'Proposta não encontrada ou indisponível.');
    }
    const data = await response.json().catch(() => null);
    if (response.status === 400 && typeof data?.mensagem === 'string') {
      throw new Error(data.mensagem);
    }
    if (!response.ok || !data) {
      const action = method === 'GET' ? 'consultar' : 'alterar';
      throw new Error(`Não foi possível ${action} a proposta. Tente novamente.`);
    }
    return data;
  } catch (error) {
    if (error.name === 'AbortError' && !signal?.aborted) {
      if (method !== 'GET') {
        throw new Error('Não foi possível confirmar a alteração. Atualize os detalhes antes de tentar novamente.');
      }
      throw new Error('A consulta demorou mais que o esperado. Tente novamente.');
    }
    if (error instanceof TypeError) {
      if (method !== 'GET') {
        throw new Error('A conexão falhou durante a alteração. Atualize os detalhes para conferir o resultado.');
      }
      throw new Error('Não foi possível conectar à API. Verifique sua conexão.');
    }
    throw error;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', abort);
  }
};

export const listPropostas = async (filters, signal) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== '' && value !== undefined && value !== null) params.set(key, String(value));
  });
  const data = await requestPropostas(`?${params}`, signal);
  if (!Array.isArray(data.dados) || !data.paginacao) {
    throw new Error('A API retornou uma listagem de propostas inválida.');
  }
  return data;
};

export const getProposta = async (id, signal) => {
  const data = await requestPropostas(`/${encodeURIComponent(id)}`, signal);
  if (!data.proposta || String(data.proposta.id) !== String(id)) {
    throw new Error('Não foi possível carregar os detalhes da proposta.');
  }
  return data.proposta;
};

export const updateProposta = async (id, payload) => {
  const data = await requestPropostas(`/${encodeURIComponent(id)}`, undefined, { method: 'PATCH', body: payload });
  if (!data.proposta || String(data.proposta.id) !== String(id)) {
    throw new Error('A API não confirmou os dados salvos. Atualize os detalhes para conferir o resultado.');
  }
  return data.proposta;
};

export const deleteProposta = async (id) => {
  const data = await requestPropostas(`/${encodeURIComponent(id)}`, undefined, { method: 'DELETE' });
  if (typeof data.mensagem !== 'string') {
    throw new Error('A API não confirmou a remoção. Atualize a lista para conferir o resultado.');
  }
  return data;
};
